/**
 * Date formatting utilities for volunteer event display.
 */
import type { VolunteerLocationItemDto } from '@sola/skippy-search-sdk/lib/schema.ts';

/**
 * Format a volunteer event's date range for display.
 *
 * Handles single-day events, multi-day events, and tour events.
 * - 'default' variant: shows date range (month only for tours)
 * - 'tour' variant for non-tour events: includes time
 *
 * Uses timeZone: 'UTC' to prevent hydration mismatches between server and client.
 */
export const formatEventDateRange = (
  volunteer: Pick<
    VolunteerLocationItemDto,
    'startDate' | 'endDate' | 'tourEvents'
  >,
  isTour: boolean,
  variant: 'default' | 'card' | 'event' = 'default',
): string => {
  const startDate = new Date(volunteer.startDate);

  const fmt = (date: Date, options: Intl.DateTimeFormatOptions) =>
    date.toLocaleDateString('en-US', { ...options, timeZone: 'UTC' });

  const fmtTime = (date: Date) =>
    date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      timeZone: 'UTC',
    });

  // Event detail page: "Thu • March 12, 2026 • 7:00 PM"
  if (variant === 'event' && !isTour) {
    return `${fmt(startDate, { weekday: 'short' })} \u2022 ${fmt(startDate, { month: 'long', day: 'numeric', year: 'numeric' })} \u2022 ${fmtTime(startDate)}`;
  }

  // Event card (tour list & event list): "Sat, Mar 15 • 7:00 PM"
  if (variant === 'card' && !isTour) {
    return `${fmt(startDate, { weekday: 'short', month: 'short', day: 'numeric' })} \u2022 ${fmtTime(startDate)}`;
  }

  // Default: date range display
  const tourEvents = volunteer.tourEvents ?? [];
  const lastEndDate = isTour
    ? tourEvents.reduce(
        (max, e) => (e?.endDate && e.endDate > max ? e.endDate : max),
        volunteer.endDate,
      )
    : volunteer.endDate;
  const endDate = new Date(lastEndDate);

  const isSameDay = fmt(startDate, {}) === fmt(endDate, {});
  const isSameYear = startDate.getUTCFullYear() === endDate.getUTCFullYear();

  const endOpts: Intl.DateTimeFormatOptions = isTour
    ? { month: 'long', year: 'numeric' }
    : { month: 'long', day: 'numeric', year: 'numeric' };

  let startOpts: Intl.DateTimeFormatOptions;
  if (isTour) {
    startOpts = { month: 'long', year: 'numeric' };
  } else if (isSameDay || !isSameYear) {
    startOpts = { month: 'long', day: 'numeric', year: 'numeric' };
  } else {
    startOpts = { month: 'long', day: 'numeric' };
  }

  if (isSameDay) {
    return fmt(startDate, startOpts);
  }

  return `${fmt(startDate, startOpts)} – ${fmt(endDate, endOpts)}`;
};

/**
 * Build a location display string, optionally showing tour location count.
 */
export const formatLocationDisplay = (
  volunteer: Pick<VolunteerLocationItemDto, 'city' | 'state' | 'tourEvents'>,
  isTour: boolean,
  isTourList: boolean,
): string => {
  const tourEvents = volunteer.tourEvents ?? [];
  const uniqueTourLocations = isTour
    ? [...new Set(tourEvents.map((e) => `${e.city}, ${e.state}`))]
    : [];
  const otherLocationCount = uniqueTourLocations.length - 1;

  if (isTour && !isTourList && otherLocationCount > 0) {
    return `${volunteer.city}, ${volunteer.state} + ${otherLocationCount} other location${otherLocationCount > 1 ? 's' : ''}`;
  }

  return `${volunteer.city}, ${volunteer.state}`;
};

/**
 * Normalize a role name for fuzzy matching.
 * Lowercases, strips hyphens, normalizes compound words, and collapses whitespace.
 */
export const normalizeRoleName = (s: string): string =>
  s
    .trim()
    .toLowerCase()
    .replaceAll('-', ' ')
    .replaceAll('setup', 'set up')
    .replaceAll('teardown', 'tear down')
    .replaceAll(/\s+/g, ' ');

/**
 * Match a content item title to a role slot count using fuzzy name matching.
 * Falls back to prefix matching (e.g. "Event Volunteer" matches "Event Facilitator").
 */
export const findMatchingRole = (
  title: string,
  roleCounts: { role: string; openSlots: number; totalSlots: number }[],
): { role: string; openSlots: number; totalSlots: number } | undefined => {
  const titleNorm = normalizeRoleName(title);

  // Direct or substring match
  const direct = roleCounts.find((rc) => {
    const roleNorm = normalizeRoleName(rc.role);
    return (
      roleNorm === titleNorm ||
      roleNorm.includes(titleNorm) ||
      titleNorm.includes(roleNorm)
    );
  });
  if (direct) {
    return direct;
  }

  // Prefix fallback: if title starts with "event", match any role starting with "event"
  const titlePrefix = titleNorm.split(' ')[0];
  if (titlePrefix === 'event') {
    return roleCounts.find((rc) =>
      normalizeRoleName(rc.role).startsWith('event'),
    );
  }

  return undefined;
};

/**
 * Roles that should only be shown when they have available slots.
 * All other roles are always visible regardless of slot availability.
 */
const conditionalRoles = new Set([
  'handout volunteer',
  'compassion experience volunteer',
  'vr experience volunteer',
  'volunteer entrance host',
]);

/**
 * Determine if a role item should be visible.
 * Conditional roles are hidden when they have no open slots.
 */
export const shouldShowRole = (
  title: string,
  roleCounts:
    | { role: string; openSlots: number; totalSlots: number }[]
    | undefined,
): boolean => {
  const titleNorm = normalizeRoleName(title);
  if (!conditionalRoles.has(titleNorm)) {
    return true;
  }
  if (!roleCounts) {
    return true; // No data yet — show all until we know
  }
  const match = findMatchingRole(title, roleCounts);
  return !!match && match.openSlots > 0;
};

/**
 * Check if a content item title matches any role in a list of role names.
 * Uses the same fuzzy matching as findMatchingRole (normalize + includes + event prefix).
 */
export const hasMatchingRole = (title: string, roles: string[]): boolean => {
  const asCounts = roles.map((r) => ({ role: r, openSlots: 0, totalSlots: 0 }));
  return !!findMatchingRole(title, asCounts);
};

/**
 * Enrich role content items with live slot count badges, or filter by indexed roles.
 *
 * When `roleCounts` is available (live data loaded), filters to roles with open slots
 * and attaches a badge showing the count.
 *
 * When `roleCounts` is not yet available, falls back to filtering items by the
 * indexed `roles` array (no badge).
 */
export const buildFilteredRoleItems = <T extends { title?: string | null }>(
  items: T[] | undefined,
  roleCounts:
    | { role: string; openSlots: number; totalSlots: number }[]
    | undefined,
  indexedRoles: string[],
  formatBadgeLabel: (openSlots: number) => string,
): (T & { badge?: { label: string; color: string; size: string } })[] => {
  if (!items) {
    return [];
  }

  if (roleCounts) {
    return items
      .filter((item) => shouldShowRole(item.title ?? '', roleCounts))
      .map((item) => {
        const matchingRole = findMatchingRole(item.title ?? '', roleCounts);
        if (!matchingRole) {
          return null;
        }
        return {
          ...item,
          badge: {
            label: formatBadgeLabel(matchingRole.openSlots),
            color: 'brand',
            size: 'sm',
          },
        };
      })
      .filter((item): item is NonNullable<typeof item> => item !== null);
  }

  return items
    .filter((item) => hasMatchingRole(item.title ?? '', indexedRoles))
    .map((item) => ({ ...item }));
};
